import { CommonModule } from '@angular/common'
import { Component, CUSTOM_ELEMENTS_SCHEMA, input, output, ViewEncapsulation } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { Subscription } from 'rxjs'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Question, Quiz, QuizResult } from '../../models/main/quiz'

@Component({
  selector: 'ddc-quiz-questions',
  templateUrl: './ddc-quiz-questions.component.html',
  styleUrls: ['./ddc-quiz-questions.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [AngularMaterialsModules, CommonModule, MatIconModule, MatProgressBarModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DdcQuizQuestionsComponent {
  quiz = input<Quiz>(undefined)
  isTaking = input<boolean>(false)
  result = input<QuizResult | null>(null)

  // Events
  onSubmitAnswer = output<{ questionId: number; answerId: number }>()
  onNextQuestion = output<void>()
  onPreviousQuestion = output<void>()
  onSubmitQuiz = output<void>()

  currentQuestionIndex = 0
  selectedAnswers = new Map<number, number>() // questionId -> answerId
  _currentTheme: string = 'light-theme'

  private themeSubscription: Subscription

  constructor(private themeService: ThemeService) {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this._currentTheme = theme
    })
  }

  ngOnDestroy(): void {
    this.themeSubscription.unsubscribe()
  }

  get currentQuestion(): Question | undefined {
    const quiz = this.quiz()
    if (!quiz?.questions) return undefined
    return quiz.questions[this.currentQuestionIndex]
  }

  get totalQuestions(): number {
    return this.quiz()?.questions?.length || 0
  }

  get progress(): number {
    if (this.totalQuestions === 0) return 0
    return ((this.currentQuestionIndex + 1) / this.totalQuestions) * 100
  }

  get isLastQuestion(): boolean {
    return this.currentQuestionIndex >= this.totalQuestions - 1
  }

  get isFirstQuestion(): boolean {
    return this.currentQuestionIndex === 0
  }

  get isAnswerSelected(): boolean {
    const currentQuestion = this.currentQuestion
    if (!currentQuestion) return false
    return this.selectedAnswers.has(currentQuestion.id)
  }

  // Theme-aware methods
  get isDarkTheme(): boolean {
    return this._currentTheme === 'dark-theme'
  }

  get isLightTheme(): boolean {
    return this._currentTheme === 'light-theme'
  }

  get isPaperTheme(): boolean {
    return this._currentTheme === 'paper-theme'
  }

  onAnswerSelect(questionId: number, answerId: number): void {
    this.selectedAnswers.set(questionId, answerId)
    this.onSubmitAnswer.emit({ questionId, answerId })
  }

  isSelected(questionId: number, answerId: number): boolean {
    return this.selectedAnswers.get(questionId) === answerId
  }

  get isResultShown(): boolean {
    return !this.isTaking() && this.result() !== null && this.result() !== undefined
  }

  isWrongAnswer(questionId: number, answerId: number): boolean {
    if (!this.isResultShown) return false
    const wrongAnswers = this.result()?.wrong_answered_questions || []
    return wrongAnswers.some(
      w => w.question.id === questionId && w.selected_answer?.id === answerId,
    )
  }

  isCorrectAnswer(questionId: number, answerId: number): boolean {
    if (!this.isResultShown) return false
    const wrongAnswers = this.result()?.wrong_answered_questions || []
    return wrongAnswers.some(w => w.question.id === questionId && w.correct_answer?.id === answerId)
  }

  onNext(): void {
    if (!this.isLastQuestion) {
      this.currentQuestionIndex++
      this.onNextQuestion.emit()
    }
  }

  onPrevious(): void {
    if (!this.isFirstQuestion) {
      this.currentQuestionIndex--
      this.onPreviousQuestion.emit()
    }
  }

  onSubmit(): void {
    // Validate that all questions have answers
    const quiz = this.quiz()
    if (!quiz?.questions) return

    const answeredCount = this.selectedAnswers.size
    const totalQuestions = quiz.questions.length

    if (answeredCount < totalQuestions) {
      const unanswerCount = totalQuestions - answeredCount
      if (!confirm(`You have ${unanswerCount} unanswered question(s). Submit anyway?`)) {
        return
      }
    }

    this.onSubmitQuiz.emit()
  }

  getLevelColor(level: string): string {
    switch (level.toLowerCase()) {
      case 'easy':
        return 'accent'
      case 'intermediate':
        return 'primary'
      case 'hard':
        return 'warn'
      default:
        return 'primary'
    }
  }

  getLevelLabel(level: string): string {
    switch (level.toLowerCase()) {
      case 'easy':
        return 'Easy'
      case 'intermediate':
        return 'Intermediate'
      case 'hard':
        return 'Hard'
      default:
        return level
    }
  }

  // Result display methods
  getScorePercentage(): number {
    const result = this.result()
    if (!result || result.total_questions === 0) return 0
    return Math.round((result.correct_count / result.total_questions) * 100)
  }

  getScoreColor(): string {
    const percentage = this.getScorePercentage()
    if (percentage >= 80) return 'accent'
    if (percentage >= 60) return 'primary'
    return 'warn'
  }

  formatTime(seconds: number | string): string {
    const numSeconds = typeof seconds === 'string' ? parseInt(seconds) : seconds
    if (isNaN(numSeconds)) return '-'

    const hours = Math.floor(numSeconds / 3600)
    const minutes = Math.floor((numSeconds % 3600) / 60)
    const secs = numSeconds % 60

    const parts: string[] = []
    if (hours > 0) parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`)

    return parts.join(' ')
  }
}
